import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { FaTrophy, FaUser } from 'react-icons/fa';

const ViewQuiz = () => {
    const { quizId } = useParams();

    const [quiz, setQuiz] = useState(null);
    const [name, setName] = useState('');
    const [agreed, setAgreed] = useState(false);
    const [joined, setJoined] = useState(false);
    const [participants, setParticipants] = useState([]);
    const [selectedAnswers, setSelectedAnswers] = useState({});
    const [answersCorrectness, setAnswersCorrectness] = useState({});
    const [submittedAnswers, setSubmittedAnswers] = useState({});
    const [timeLeft, setTimeLeft] = useState(0);
    const [questionStartTime, setQuestionStartTime] = useState(null);
    const [results, setResults] = useState([]);
    const [showResults, setShowResults] = useState(false);

    // Fetch participants on load
    useEffect(() => {
        const fetchParticipants = async () => {
            try {
                const res = await axios.get(`https://rvapi.emedha.in/pages/api/api/participants/${quizId}`);
                const allParticipants = res.data || [];
                setParticipants(allParticipants);
                if (allParticipants.some(p => p.flag === 1)) {
                    setJoined(true);
                }
            } catch (err) {
                console.error('Error fetching participants:', err);
            }
        };
        fetchParticipants();
    }, [quizId]);

    // Fetch quiz every 20 seconds
    useEffect(() => {
        if (!joined) return;
        const fetchQuiz = async () => {
            try {
                const res = await axios.get(`https://rvapi.emedha.in/pages/api/pages/api/api/quiz/${quizId}`);
                setQuiz(res.data);
            } catch (err) {
                console.error('Failed to load quiz:', err);
            }
        };
        fetchQuiz();
        const intervalId = setInterval(fetchQuiz, 20000);
        return () => clearInterval(intervalId);
    }, [joined, quizId]);

    // Track active question
    useEffect(() => {
        if (!quiz) return;
        const active = quiz.questions
            .map((q, index) => ({ q, index }))
            .find(({ q, index }) => q.flag === 1 && !submittedAnswers[index]);

        if (active) {
            setTimeLeft(active.q.timer);
            setQuestionStartTime(Date.now());
        } else {
            setTimeLeft(0);
        }
    }, [quiz, submittedAnswers]);

    // Countdown timer
    useEffect(() => {
        if (timeLeft <= 0) return;
        const interval = setInterval(() => {
            setTimeLeft(prev => {
                if (prev <= 1) {
                    clearInterval(interval);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
        return () => clearInterval(interval);
    }, [timeLeft]);

    // Join quiz
    const handleJoin = async () => {
        if (!name.trim()) return alert('Please enter your name.');
        if (!agreed) return alert('Please agree to the terms.');
        try {
            await axios.post('https://rvapi.emedha.in/pages/api/api/participants', { name, quizId });
            setJoined(true);
        } catch (err) {
            alert('Failed to join quiz.');
            console.error(err);
        }
    };

    const getInitials = (fullName) => {
        const words = fullName.trim().split(' ');
        return words.length === 1 ? words[0][0].toUpperCase() : (words[0][0] + words[words.length - 1][0]).toUpperCase();
    };

    const handleSelectAnswer = (questionIndex, optionIndex) => {
        setSelectedAnswers(prev => ({ ...prev, [questionIndex]: optionIndex }));
        setAnswersCorrectness(prev => ({ ...prev, [questionIndex]: null }));
    };

    const handleCheckAnswer = async (questionIndex) => {
        const selected = selectedAnswers[questionIndex];
        if (selected === undefined || selected === null) return alert('Please select an answer.');
        if (!name.trim()) return alert('Enter your name and join first.');

        const correct = quiz.questions[questionIndex].correctAnswer === selected;
        const timeTaken = `${Math.ceil((Date.now() - questionStartTime) / 1000)}s`;

        setAnswersCorrectness(prev => ({ ...prev, [questionIndex]: correct }));
        setSubmittedAnswers(prev => ({ ...prev, [questionIndex]: true }));
        setTimeLeft(0);

        try {
            await axios.post('https://rvapi.emedha.in/pages/api/api/submit-answer', {
                quizId,
                name,
                questionIndex,
                selectedAnswer: selected,
                isCorrect: correct,
                timeTaken
            });
        } catch (err) {
            console.error('Failed to submit answer:', err);
        }
    };

    // Get results after last question is done
    useEffect(() => {
        if (!quiz) return;
        const allAnswered = quiz.questions.every(q => q.flag === 1);

        if (allAnswered) {
            const fetchResults = async () => {
                try {
                    const res = await axios.get(`https://rvapi.emedha.in/pages/api/api/quiz_answers/${quizId}`);
                    const data = res.data;

                    const sorted = data.map(p => ({
                        name: p.name,
                        totalCorrect: p.totalCorrect,
                        answers: p.answers || [],
                        firstSubmittedAt: new Date(p.firstSubmittedAt)
                    }))
                        .sort((a, b) => {
                            if (b.totalCorrect !== a.totalCorrect) {
                                return b.totalCorrect - a.totalCorrect;
                            }
                            return a.firstSubmittedAt - b.firstSubmittedAt;
                        });

                    setResults(sorted);
                    setShowResults(true);
                } catch (err) {
                    console.error('Failed to fetch results:', err);
                }
            };

            fetchResults();
        }

    }, [quiz, quizId]);

    // Join screen
    if (!joined) {
        return (
            <div className="container mt-5">
                <h2 className="fw-bold"><FaTrophy className="text-warning mb-2 me-2" /> Quiz</h2>
                <div className="input-group w-100 mb-3">
                    <span className="input-group-text">{name.trim() ? <strong>{getInitials(name)}</strong> : <FaUser />}</span>
                    <input type="text" className="form-control" placeholder="Your name"
                        value={name} onChange={(e) => setName(e.target.value)} />
                    <button className="btn btn-primary" type="button" onClick={handleJoin}>Join</button>
                </div>
                <div className="form-check mt-2">
                    <input className="form-check-input" type="checkbox" checked={agreed}
                        onChange={() => setAgreed(!agreed)} id="agreeCheck" />
                    <label className="form-check-label" htmlFor="agreeCheck">
                        I agree to use my name while using Quiz.emedha.
                    </label>
                </div>
            </div>
        );
    }

    if (!quiz) return <div className="container mt-5">Loading quiz...</div>;

    // No active question
    const activeQuestion = quiz.questions
        .map((q, index) => ({ q, index }))
        .find(({ q, index }) => q.flag === 1 && !submittedAnswers[index]);

    if (!activeQuestion) {
        const moreComing = quiz.questions.some(q => q.flag === 0);

        return (
            <div className="container mt-5 text-center">
                <h3>{quiz.title}</h3>
                <div className={`alert mt-4 ${moreComing ? 'alert-warning' : 'alert-info'}`}>
                    {moreComing
                        ? 'Please wait for the next question to appear.'
                        : 'No more questions! Thanks for participating. Wait for result.'}
                </div>

                {!moreComing && showResults && (
                    <div className="mt-4">
                        <h4 className="fw-bold mb-3">🏆 Leaderboard</h4>
                        <table className="table table-bordered">
                            <thead className="table-dark">
                                <tr>
                                    <th>Rank</th>
                                    <th>Name</th>
                                    <th>Correct</th>
                                    <th>Score</th>
                                </tr>
                            </thead>
                            <tbody>
                                {results.map((r, i) => (
                                    <tr key={i}>
                                        <td>{i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : i + 1}</td>
                                        <td>{r.name}</td>
                                        <td>{r.totalCorrect}</td>
                                        <td>
                                            <div className="progress" style={{ height: '20px' }}>
                                                <div className="progress-bar bg-success" role="progressbar"
                                                    style={{
                                                        width: `${(r.totalCorrect / r.answers.length) * 100}%`
                                                    }}>
                                                    {(r.totalCorrect / r.answers.length) * 100}%
                                                </div>
                                            </div>
                                        </td>

                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        );
    }

    // Active question UI
    const { q: question, index } = activeQuestion;
    const selected = selectedAnswers[index];
    const correct = answersCorrectness[index];
    const submitted = submittedAnswers[index];

    return (
        <div className="container mt-4">
            <h3>{quiz.title}</h3>
            <div className="card shadow-lg border-0 mt-4">
                <div className="card-body p-4">
                    <h4 className="fw-bold text-primary">{question.question}</h4>
                    <div className="progress mb-3" style={{ height: '20px' }}>
                        <div className="progress-bar bg-dark" role="progressbar"
                            style={{ width: `${(timeLeft / question.timer) * 100}%`, transition: 'width 1s linear' }}>
                            {timeLeft}s
                        </div>
                    </div>
                    <form>
                        {question.options.map((opt, i) => (
                            <div key={i} className={`form-check mb-3 p-3 rounded border ${selected === i ? 'border-primary bg-light' : 'border-secondary'}`}>
                                <input type="radio" className="form-check-input"
                                    id={`option-${index}-${i}`}
                                    name={`quiz-option-${index}`}
                                    checked={selected === i}
                                    onChange={() => handleSelectAnswer(index, i)}
                                    disabled={submitted || timeLeft === 0} />
                                <label className="form-check-label ms-2" htmlFor={`option-${index}-${i}`}>{opt}</label>
                            </div>
                        ))}
                    </form>
                    {!submitted && (
                        <button className="btn btn-primary mt-3 w-100"
                            onClick={() => handleCheckAnswer(index)}
                            disabled={selected === undefined || timeLeft === 0}>
                            ✅ Submit Answer
                        </button>
                    )}
                    {submitted && correct !== null && (
                        <div className={`alert mt-4 fw-bold text-center ${correct ? 'alert-success' : 'alert-danger'}`}>
                            {correct ? '🎉 Correct!' : '❌ Incorrect. Try again next time.'}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ViewQuiz;
